import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2, CreditCard } from "lucide-react";
import DobField from "@/components/DobField";
import AvatarUpload from "@/components/AvatarUpload";

export default function PublicRegister() {
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    player_name: "", dob: "", parent_name: "", parent_email: "", parent_phone: "",
    medical_info: "", emergency_contact: "", photo: "", waiver_accepted: false, team_preference: "",
    jersey_number: "", position: "", height: "", weight: "", allergies: "", address: "", consent_document: "",
  });
  const [isAdult, setIsAdult] = useState(false);

  const ageFromDob = (d) => {
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const dt = new Date(d); if (isNaN(dt)) return null;
    const t = new Date(); let a = t.getFullYear() - dt.getFullYear();
    const m = t.getMonth() - dt.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < dt.getDate())) a--;
    return a;
  };
  const onDob = (v) => { set("dob", v); const a = ageFromDob(v); if (a !== null) { setIsAdult(a >= 18); if (a >= 18) set("parent_name", ""); } };
  const toggleAdult = (on) => { setIsAdult(on); if (on) set("parent_name", ""); };

  useEffect(() => {
    api.get(`/clubs/${clubId}/public`).then((r) => setClub(r.data)).catch(() => setClub(false));
    // Handle return from Stripe card-setup (token-gated)
    const params = new URLSearchParams(window.location.search);
    const reg = params.get("reg");
    const token = params.get("token");
    if (reg && token && params.get("card_session")) {
      api.post("/registrations/card-confirm", { reg_id: reg, session_id: params.get("card_session"), token })
        .then(() => setDone(true))
        .catch(() => toast.error("We couldn't confirm your card. Your registration was still submitted."))
        .finally(() => window.history.replaceState(null, "", `/join/${clubId}`));
    } else if (reg && params.get("card_cancel")) {
      setDone(true);
      window.history.replaceState(null, "", `/join/${clubId}`);
    }
  }, [clubId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onPhoto = (url) => { if (url) set("photo", url); };
  const onConsent = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader(); reader.onload = () => set("consent_document", reader.result); reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.waiver_accepted) return toast.error("Please accept the liability waiver");
    setLoading(true);
    try {
      const { data: reg } = await api.post("/registrations", { club_id: clubId, ...form, parent_name: isAdult ? "" : form.parent_name });
      if (club?.require_card_on_registration) {
        const { data } = await api.post(`/registrations/${reg.id}/card-setup`, { origin_url: window.location.origin, token: reg.card_token });
        window.location.href = data.checkout_url;
        return;
      }
      setDone(true);
    }
    catch (err) { toast.error(apiErr(err)); setLoading(false); }
  };

  if (club === false) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Club not found.</div>;
  if (!club) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="text-center max-w-md">
        <CheckCircle2 size={56} className="mx-auto text-secondary mb-5" />
        <h1 className="font-heading font-extrabold text-2xl">Registration submitted!</h1>
        <p className="text-muted-foreground mt-2">Thanks for registering with {club.name}. A coach will review and confirm your athlete's spot.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grain py-10 px-6">
      <div className="max-w-lg mx-auto">
        <div className="flex flex-col items-center text-center mb-8">
          {club.logo ? <img src={club.logo} alt="" className="h-16 w-16 rounded-2xl object-cover border border-border" /> : <Logo size={44} showText={false} />}
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-4">{club.name}</h1>
          <p className="text-muted-foreground mt-1 capitalize">{club.sport?.replace("_", " ")} · Athlete registration</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4">
          <AvatarUpload value={form.photo} onFile={onPhoto} testid="reg-photo-input" label="Athlete photo" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>{isAdult ? "Your name *" : "Athlete name *"}</Label><Input required value={form.player_name} onChange={(e) => set("player_name", e.target.value)} className="mt-1.5 h-11" data-testid="reg-player-name" /></div>
            <div><Label>Date of birth</Label><DobField value={form.dob} onChange={onDob} testid="reg-dob" /></div>
            <div><Label>Team preference</Label><Input value={form.team_preference} onChange={(e) => set("team_preference", e.target.value)} placeholder="U12" className="mt-1.5 h-11" /></div>
            <label className="sm:col-span-2 flex items-center gap-3 text-sm bg-accent/40 rounded-lg p-3 cursor-pointer" data-testid="reg-adult-label">
              <Checkbox checked={isAdult} onCheckedChange={(v) => toggleAdult(!!v)} data-testid="reg-adult-toggle" />
              <span className="text-muted-foreground">I'm an adult athlete (18+) registering myself — no parent or guardian needed.</span>
            </label>
            {!isAdult && <div><Label>Parent name *</Label><Input required value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} className="mt-1.5 h-11" data-testid="reg-parent-name" /></div>}
            <div><Label>{isAdult ? "Your email *" : "Parent email *"}</Label><Input required type="email" value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} className="mt-1.5 h-11" data-testid="reg-parent-email" /></div>
            <div><Label>{isAdult ? "Your phone" : "Parent phone"}</Label><Input value={form.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} className="mt-1.5 h-11" data-testid="reg-parent-phone" /></div>
            <div><Label>Emergency contact</Label><Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label>Jersey number</Label><Input value={form.jersey_number} onChange={(e) => set("jersey_number", e.target.value)} placeholder="10" className="mt-1.5 h-11" data-testid="reg-jersey" /></div>
            <div><Label>Position</Label><Input value={form.position} onChange={(e) => set("position", e.target.value)} placeholder="Midfield" className="mt-1.5 h-11" data-testid="reg-position" /></div>
            <div><Label>Height</Label><Input value={form.height} onChange={(e) => set("height", e.target.value)} placeholder="150 cm" className="mt-1.5 h-11" data-testid="reg-height" /></div>
            <div><Label>Weight</Label><Input value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="45 kg" className="mt-1.5 h-11" data-testid="reg-weight" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11" data-testid="reg-address" /></div>
            <div className="sm:col-span-2"><Label>Allergies</Label><Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="None" className="mt-1.5 h-11" data-testid="reg-allergies" /></div>
            <div className="sm:col-span-2"><Label>Medical information</Label><Textarea value={form.medical_info} onChange={(e) => set("medical_info", e.target.value)} className="mt-1.5" rows={3} /></div>
            <div className="sm:col-span-2"><Label>Signed consent / waiver document (optional)</Label>
              <input type="file" accept=".pdf,image/*" onChange={onConsent} className="mt-1.5 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:text-white file:px-3 file:py-2 file:text-sm file:cursor-pointer" data-testid="reg-consent-file" />
              {form.consent_document && <p className="text-xs text-emerald-500 mt-1" data-testid="reg-consent-attached">✓ Document attached</p>}
            </div>
          </div>
          <label className="flex items-start gap-3 text-sm bg-accent/50 rounded-lg p-3 cursor-pointer">
            <Checkbox checked={form.waiver_accepted} onCheckedChange={(v) => set("waiver_accepted", !!v)} data-testid="reg-waiver-checkbox" className="mt-0.5" />
            <span className="text-muted-foreground">{isAdult ? `I accept the liability waiver and consent to participating in ${club.name} activities. ` : `I accept the liability waiver and consent to my child participating in ${club.name} activities. `}I have read and agree to the <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline" data-testid="reg-privacy-link">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noreferrer" className="text-primary underline" data-testid="reg-terms-link">Terms &amp; Conditions</a>.</span>
          </label>
          {club.require_card_on_registration && (
            <div className="flex items-start gap-3 text-sm bg-primary/5 border border-primary/30 rounded-lg p-3" data-testid="reg-card-notice">
              <CreditCard size={16} className="text-primary mt-0.5 shrink-0" />
              <span className="text-muted-foreground">After submitting, you'll securely save a payment card (via Stripe) so future fees can be charged automatically. You won't be charged now.</span>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-12 brand-gradient text-white border-0 hover:opacity-90 text-base" data-testid="reg-submit-btn">
            {loading ? "Submitting..." : club.require_card_on_registration ? "Submit & save card" : "Submit registration"}
          </Button>
        </form>
      </div>
    </div>
  );
}
